'use client'

import { useEffect } from 'react'

import { track } from '@/utilities/analytics'

const MAX_LINK_TEXT = 80
const DOWNLOAD_EXTENSIONS = /\.(pdf|docx?|xlsx?|pptx?|zip|dwg|dxf|step|stp|stl|ai|eps|jpg|jpeg|png|svg)(\?|#|$)/i

function deriveLocation(anchor: HTMLAnchorElement): string {
  const tagged = anchor.closest<HTMLElement>('[data-analytics-location]')
  if (tagged) return tagged.dataset.analyticsLocation || 'body'
  if (anchor.closest('footer')) return 'footer'
  if (anchor.closest('header')) return 'header'
  if (anchor.closest('nav')) return 'nav'
  return 'body'
}

function trimText(value: string | null | undefined): string | undefined {
  if (!value) return undefined
  const collapsed = value.replace(/\s+/g, ' ').trim()
  if (!collapsed) return undefined
  return collapsed.length > MAX_LINK_TEXT ? collapsed.slice(0, MAX_LINK_TEXT) : collapsed
}

function fileNameFromHref(href: string): string {
  try {
    const url = new URL(href, window.location.origin)
    const parts = url.pathname.split('/').filter(Boolean)
    return parts[parts.length - 1] || href
  } catch {
    return href
  }
}

/**
 * Site-wide delegated click listener. Captures:
 * - tel: / mailto: anchors → phone_click / email_click
 * - clicks inside [data-cta-block] containers → cta_clicked
 * - clicks inside [data-card-type] containers → project_card_clicked / product_card_clicked
 * - clicks on [data-download], or links whose href ends in a known doc extension → download_clicked
 *
 * Avoids touching individual link components (CMSLink is a Server Component)
 * and gives a single audit point for click analytics.
 */
export default function AnalyticsAutoTracker() {
  useEffect(() => {
    const fireCardEvent = (
      card: HTMLElement,
      linkText: string | undefined,
    ) => {
      const cardType = card.dataset.cardType
      if (cardType === 'project') {
        track('project_card_clicked', {
          project_slug: card.dataset.cardSlug,
          source_block: card.dataset.cardBlock,
          link_text: linkText,
        })
      } else if (cardType === 'product') {
        track('product_card_clicked', {
          product_slug: card.dataset.cardSlug,
          source_block: card.dataset.cardBlock,
          link_text: linkText,
        })
      }
    }

    const onClick = (event: MouseEvent) => {
      const eventTarget = event.target
      if (!(eventTarget instanceof Element)) return

      // Accordion / FAQ: any [aria-expanded] trigger. At click time the
      // aria-expanded attribute still reflects the OLD value, so "false" means
      // about-to-open. This catches Accordion, FAQ, LexicalAccordion, LexicalFAQ.
      const expander = eventTarget.closest<HTMLElement>('[aria-expanded]')
      if (expander && expander.getAttribute('aria-expanded') === 'false') {
        const label = expander.textContent?.replace(/\s+/g, ' ').trim().slice(0, MAX_LINK_TEXT)
        track('accordion_opened', { item_label: label || undefined })
        // fall through — accordion triggers are usually <button>s that aren't
        // also anchors, but we want to allow the rest of the chain to evaluate
        // in the unlikely edge case.
      }

      const anchor = eventTarget.closest('a') as HTMLAnchorElement | null

      if (!anchor) {
        // Card-as-button pattern: clickable wrapper without an inner <a>
        // (e.g. SimpleProjectCard uses router.push on the outer div).
        const card = eventTarget.closest<HTMLElement>('[data-card-type]')
        if (card) fireCardEvent(card, undefined)
        return
      }

      const href = anchor.getAttribute('href')
      if (!href) return

      if (href.startsWith('tel:')) {
        track('phone_click', {
          location: deriveLocation(anchor),
          link_text: trimText(anchor.textContent),
        })
        return
      }
      if (href.startsWith('mailto:')) {
        track('email_click', {
          location: deriveLocation(anchor),
          link_text: trimText(anchor.textContent),
        })
        return
      }

      // Downloads: explicit opt-in via [data-download] OR known doc extension.
      const downloadContainer = anchor.closest<HTMLElement>('[data-download]')
      const isLikelyDownload =
        downloadContainer != null || (anchor.hasAttribute('download') || DOWNLOAD_EXTENSIONS.test(href))
      if (isLikelyDownload) {
        track('download_clicked', {
          file_url: href,
          file_name: fileNameFromHref(href),
          download_block: downloadContainer?.dataset.download || 'unknown',
          link_text: trimText(anchor.textContent),
        })
        // fall through — a download link could also live inside a card,
        // but we treat the download event as the primary signal.
        return
      }

      // Project / product cards.
      const card = anchor.closest<HTMLElement>('[data-card-type]')
      if (card) {
        fireCardEvent(card, trimText(anchor.textContent))
        return
      }

      // CTAs (any anchor inside a [data-cta-block] container).
      const ctaBlock = anchor.closest<HTMLElement>('[data-cta-block]')
      if (ctaBlock) {
        track('cta_clicked', {
          cta_block: ctaBlock.dataset.ctaBlock,
          cta_label: trimText(anchor.textContent),
          cta_destination: href,
          location: deriveLocation(anchor),
        })
      }
    }
    // Native <video> play events bubble through capture. One listener catches
    // every video block (FullWidthMedia, MediaBlock, ApprenticeshipSplitVideos, etc.).
    const playedRefs = new WeakSet<HTMLVideoElement>()
    const onPlay = (event: Event) => {
      const v = event.target
      if (!(v instanceof HTMLVideoElement)) return
      if (playedRefs.has(v)) return
      playedRefs.add(v)
      const source = v.currentSrc || v.src || undefined
      track('video_played', {
        video_source: source,
        video_duration: Number.isFinite(v.duration) ? Math.round(v.duration) : undefined,
      })
    }

    document.addEventListener('click', onClick, true)
    document.addEventListener('play', onPlay, true)
    return () => {
      document.removeEventListener('click', onClick, true)
      document.removeEventListener('play', onPlay, true)
    }
  }, [])

  return null
}
